import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from '@/i18n'
import {
  Database,
  Link2,
  ChevronRight,
  History,
  Download,
  Save,
  Workflow,
  Loader2,
} from 'lucide-react'
import { toast } from 'sonner'
import AppSidebar from '@/components/AppSidebar'
import DataSidebar from '@/components/DataSidebar'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useAppStore } from '@/store'
import { apiService, type EntityMatchCandidate, type SavedEntityMatch } from '@/service/api'
import TagListPanel from './TagListPanel'
import MatchedFoundPanel, { rowId, type Decision } from './MatchedFoundPanel'
import { isWorkspaceSaveAllowed } from './saveEligibility'
import { PAGE_HEADER } from '@/components/layout/pageHeader'

const MatchEntityPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const workspaceId = useAppStore((s) => s.context_id)

  const docIds = useMemo(() => {
    const raw = params.get('docs') ?? ''
    return new Set(
      raw.split(',').map((s) => s.trim()).filter(Boolean),
    )
  }, [params])

  // Data Catalog flow (launched via /data-catalog/match-entity?docs=<csv>)
  // routes through the tenant-scoped endpoint: the server resolves tags
  // from the selected docs and matches against the tenant library
  // (expand-when-empty). The legacy board-chat flow keeps using the
  // workspace endpoint.
  const isDataCatalogMode = docIds.size > 0

  const [pidTags, setPidTags] = useState<string[]>([])
  const [tsTags, setTsTags] = useState<string[]>([])
  const [tagsLoading, setTagsLoading] = useState(true)
  const [tagsError, setTagsError] = useState<string | null>(null)
  const [minConfidence, setMinConfidence] = useState('0.5')
  const [running, setRunning] = useState(false)
  const [matches, setMatches] = useState<EntityMatchCandidate[]>([])
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [decisions, setDecisions] = useState<Record<string, Decision>>({})
  // Decisions already persisted as tenant feedback, keyed by row id — used to
  // keep the "Save Decisions" action from re-submitting unchanged rows.
  const [savedDecisions, setSavedDecisions] = useState<Record<string, Decision>>({})
  const [saving, setSaving] = useState(false)

  // Fetch P&ID + Time Series tags for display.
  //   - Data Catalog mode: resolve tags by document_id via the tenant
  //     preview endpoint. The workspace-scoped endpoints inner-join
  //     through workspace_documents, so docs uploaded via Data Catalog
  //     and never attached to a board are invisible to them.
  //   - Legacy board-chat mode: load from the active workspace.
  useEffect(() => {
    let cancelled = false

    if (isDataCatalogMode) {
      void (async () => {
        try {
          setTagsLoading(true)
          const resp = await apiService.previewTenantMatchTags(Array.from(docIds))
          if (cancelled) return
          setPidTags(resp.pid_tags)
          setTsTags(resp.ts_tags)
          setTagsError(null)
        } catch (err) {
          if (cancelled) return
          setTagsError(err instanceof Error ? err.message : 'Failed to load tags')
        } finally {
          if (!cancelled) setTagsLoading(false)
        }
      })()
      return () => {
        cancelled = true
      }
    }

    if (!workspaceId) {
      setTagsError('No workspace selected. Open a board first.')
      setTagsLoading(false)
      return
    }
    void (async () => {
      try {
        setTagsLoading(true)
        const [pnidResp, tsResp] = await Promise.all([
          apiService.getWorkspacePnidTags(workspaceId),
          apiService.getWorkspaceTimeseriesTags(workspaceId),
        ])
        if (cancelled) return
        setPidTags(Array.from(new Set(pnidResp.tags.map((t) => t.tag))))
        setTsTags(tsResp.tags)
        setTagsError(null)
      } catch (err) {
        if (cancelled) return
        setTagsError(err instanceof Error ? err.message : 'Failed to load tags')
      } finally {
        if (!cancelled) setTagsLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [workspaceId, docIds, isDataCatalogMode])

  const handleRunMatching = useCallback(async () => {
    const threshold = Number(minConfidence)
    if (!Number.isFinite(threshold) || threshold < 0 || threshold > 1) {
      toast.error(
        t('match_entity:invalid_confidence', {
          defaultValue: 'Min Confidence must be between 0 and 1.',
        }),
      )
      return
    }

    setRunning(true)
    try {
      if (isDataCatalogMode) {
        // Tenant-scoped flow. Server resolves tags from selected docs and
        // auto-saves matches with workspace_id=NULL (dedup index handles
        // duplicates). Response includes the saved rows for display.
        const resp = await apiService.runTenantEntityMatching({
          document_ids: Array.from(docIds),
          confidence_threshold: threshold,
        })
        // Map TenantMatchResultRow → EntityMatchCandidate for the existing
        // MatchedFoundPanel which expects the candidate shape.
        const mapped: EntityMatchCandidate[] = resp.matches.map((row) => ({
          // FS-520: carry the persisted tenant-match row id so feedback has a
          // valid target (the run auto-saves these tenant-scoped).
          id: row.id,
          pid_tag: row.pid_tag,
          ts_tag: row.ts_tag,
          confidence: row.confidence,
          match_method: row.match_method,
          reasoning: row.reasoning,
          equipment_type: row.equipment_type ?? null,
          pid_document_id: row.pid_document_id ?? null,
        }))
        setMatches(mapped)
        setSelectedIds(new Set())
        // Restore prior accept/reject decisions for these matches on reopen.
        try {
          const feedback = await apiService.getTenantMatchDecisions()
          const byMatchId = new Map(feedback.map((f) => [f.match_id, f.action]))
          const restored: Record<string, Decision> = {}
          mapped.forEach((m, i) => {
            const action = m.id ? byMatchId.get(m.id) : undefined
            if (action === 'accepted' || action === 'rejected') {
              restored[rowId(m, i)] = action
            }
          })
          // Restored decisions are already persisted, so they start as "saved".
          setDecisions(restored)
          setSavedDecisions(restored)
        } catch {
          setDecisions({})
          setSavedDecisions({})
          toast.error(
            t('match_entity:restore_failed', {
              defaultValue: 'Could not restore previous decisions.',
            }),
          )
        }
        if (resp.skipped) {
          toast.error(
            t('match_entity:matching_skipped', {
              defaultValue: 'Matching skipped: {{reason}}',
              reason: resp.skip_reason || 'no usable tags',
            }),
          )
        } else {
          toast.success(
            t('match_entity:matching_success', {
              defaultValue: '{{count}} match(es) found.',
              count: mapped.length,
            }),
          )
        }
        return
      }

      // Legacy board-chat flow — workspace-scoped.
      if (!workspaceId) return
      if (pidTags.length === 0 || tsTags.length === 0) {
        toast.error(
          t('match_entity:no_tags', {
            defaultValue: 'Cannot run matching without both P&ID and Time Series tags.',
          }),
        )
        return
      }
      const resp = await apiService.runEntityMatching(workspaceId, {
        pid_tags: pidTags,
        ts_tags: tsTags,
        confidence_threshold: threshold,
        include_unmatched: false,
      })
      setMatches(resp.matches)
      setSelectedIds(new Set())
      setDecisions({})
      toast.success(
        t('match_entity:matching_success', {
          defaultValue: '{{count}} match(es) found.',
          count: resp.matches.length,
        }),
      )
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : t('match_entity:matching_failed', { defaultValue: 'Matching failed.' }),
      )
    } finally {
      setRunning(false)
    }
  }, [workspaceId, minConfidence, pidTags, tsTags, t, isDataCatalogMode, docIds])

  // Auto-run matching once the tag preview resolves, using the default 0.5
  // confidence threshold — so matches surface on open without a manual
  // "Run Matching" click. Guarded by a ref so it fires at most once per open,
  // and never while a run is in flight or after results already exist.
  const autoRanRef = useRef(false)
  useEffect(() => {
    if (
      autoRanRef.current ||
      tagsLoading ||
      tagsError ||
      running ||
      matches.length > 0 ||
      pidTags.length === 0 ||
      tsTags.length === 0
    ) {
      return
    }
    autoRanRef.current = true
    void handleRunMatching()
  }, [tagsLoading, tagsError, running, matches.length, pidTags.length, tsTags.length, handleRunMatching])

  const handleToggleSelect = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const handleToggleSelectAll = useCallback(() => {
    setSelectedIds((prev) => {
      const allIds = matches.map((m, i) => rowId(m, i))
      const allSelected = allIds.length > 0 && allIds.every((id) => prev.has(id))
      if (allSelected) return new Set()
      return new Set(allIds)
    })
  }, [matches])

  const handleDecide = useCallback((id: string, decision: Decision) => {
    setDecisions((prev) => ({ ...prev, [id]: decision }))
  }, [])

  // Build the list of selected matches in render order so row indices stay
  // stable across decide/select toggles.
  const selectedMatches = useMemo(
    () =>
      matches
        .map((m, i) => ({ match: m, id: rowId(m, i) }))
        .filter((x) => selectedIds.has(x.id)),
    [matches, selectedIds],
  )

  // Decided rows whose decision isn't yet persisted (new or changed since the
  // last save) — the unit the Data Catalog tenant feedback action submits.
  // Matches are already auto-saved on run, so only the decision is persisted.
  const unsavedDecisions = useMemo(
    () =>
      matches
        .map((m, i) => {
          const id = rowId(m, i)
          return { match: m, id, decision: decisions[id] ?? null }
        })
        .filter(
          (x) => x.decision !== null && !!x.match.id && x.decision !== savedDecisions[x.id],
        ),
    [matches, decisions, savedDecisions],
  )

  const handleSaveSelected = useCallback(async () => {
    // FS-520: never write workspace-scoped matches in Data Catalog mode — the
    // run already auto-saved tenant-scoped, and `context_id` here is a stale
    // unrelated board.
    if (!isWorkspaceSaveAllowed(isDataCatalogMode, workspaceId) || selectedMatches.length === 0) return
    setSaving(true)
    try {
      const saved: SavedEntityMatch[] = await apiService.saveEntityMatches(
        workspaceId,
        selectedMatches.map((x) => x.match),
      )
      // After save, post any explicit accept/reject decisions as feedback.
      // The saved array is returned in the same order as we submitted.
      const feedbacks = saved.flatMap((row, i) => {
        const local = selectedMatches[i]
        if (!local) return []
        const decision = decisions[local.id]
        if (!decision) return []
        return [
          apiService.submitMatchFeedback(workspaceId, row.id, { action: decision }),
        ]
      })
      if (feedbacks.length > 0) {
        await Promise.allSettled(feedbacks)
      }
      toast.success(
        t('match_entity:save_success', {
          defaultValue: 'Saved {{count}} match(es).',
          count: saved.length,
        }),
      )
      // Drop the saved rows from the working list so the user can iterate.
      const savedIds = new Set(selectedMatches.map((x) => x.id))
      setMatches((prev) => prev.filter((m, i) => !savedIds.has(rowId(m, i))))
      setSelectedIds(new Set())
      setDecisions((prev) => {
        const next = { ...prev }
        for (const id of savedIds) delete next[id]
        return next
      })
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : t('match_entity:save_failed', { defaultValue: 'Failed to save matches.' }),
      )
    } finally {
      setSaving(false)
    }
  }, [workspaceId, selectedMatches, decisions, t, isDataCatalogMode])

  // Data Catalog mode: persist accept/reject decisions as tenant-scoped
  // feedback against the auto-saved match rows. There's no workspace to save
  // to here, so this replaces the (disabled) workspace save.
  const handleSubmitDecisions = useCallback(async () => {
    if (!isDataCatalogMode || unsavedDecisions.length === 0) return
    setSaving(true)
    // Promise.allSettled never rejects, so per-row failures are counted below
    // rather than caught here; finally just clears the saving flag.
    try {
      const results = await Promise.allSettled(
        unsavedDecisions.map((x) =>
          apiService.submitTenantMatchFeedback(x.match.id as string, {
            action: x.decision as 'accepted' | 'rejected',
          }),
        ),
      )
      // Mark the rows that persisted as saved so a second click doesn't re-fire
      // (and the button disables once everything is saved).
      const persisted: Record<string, Decision> = {}
      unsavedDecisions.forEach((x, i) => {
        if (results[i].status === 'fulfilled') persisted[x.id] = x.decision
      })
      setSavedDecisions((prev) => ({ ...prev, ...persisted }))
      const failed = results.filter((r) => r.status === 'rejected').length
      if (failed > 0) {
        toast.error(
          t('match_entity:decisions_partial', {
            defaultValue: 'Saved {{ok}} of {{total}} decision(s).',
            ok: unsavedDecisions.length - failed,
            total: unsavedDecisions.length,
          }),
        )
      } else {
        toast.success(
          t('match_entity:decisions_saved', {
            defaultValue: 'Saved {{count}} decision(s).',
            count: unsavedDecisions.length,
          }),
        )
      }
    } finally {
      setSaving(false)
    }
  }, [isDataCatalogMode, unsavedDecisions, t])

  const handleExportSelected = useCallback(() => {
    if (selectedMatches.length === 0) return
    const escape = (val: string | number | null | undefined) => {
      const s = String(val ?? '')
      if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`
      return s
    }
    const header = [
      'P&ID Tag',
      'Time Series Tag',
      'Confidence',
      'Match Method',
      'Validation',
      'Quality',
      'Equipment',
      'Decision',
    ]
    const rows = selectedMatches.map(({ match: m, id }) => [
      m.pid_tag,
      m.ts_tag,
      m.confidence,
      m.match_method,
      m.reasoning,
      m.match_quality ?? '',
      m.equipment_type ?? '',
      decisions[id] ?? '',
    ])
    const csv = [header, ...rows].map((r) => r.map(escape).join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `entity-matches-${new Date().toISOString().slice(0, 10)}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }, [selectedMatches, decisions])

  return (
    <div className="flex h-screen">
      <AppSidebar />
      <DataSidebar />
      <div className="flex flex-1 flex-col min-w-0">
        {/* Header */}
        <header className={PAGE_HEADER}>
          <nav className="flex items-center gap-2 flex-1 text-sm">
            <button
              type="button"
              onClick={() => navigate('/data-catalog')}
              className="inline-flex items-center gap-1.5 text-muted-foreground hover:text-foreground"
            >
              <Database className="size-4" />
              {t('nav:menu.data', 'Data')}
            </button>
            <ChevronRight className="size-3.5 text-muted-foreground" />
            <span className="inline-flex items-center gap-1.5 font-medium text-foreground">
              <Link2 className="size-4 text-muted-foreground" />
              {t('match_entity:breadcrumb.current', { defaultValue: 'Match Entity' })}
            </span>
          </nav>
        </header>

        {/* Main scroll area */}
        <main className="flex-1 overflow-auto px-6 py-6 flex flex-col gap-4">
          <TagListPanel
            pidTags={pidTags}
            tsTags={tsTags}
            loading={tagsLoading}
            error={tagsError}
          />

          {/* Min Confidence + Run Matching */}
          <div className="flex items-center justify-end gap-3">
            <label className="flex items-center gap-2 text-sm">
              <span className="text-muted-foreground">
                {t('match_entity:min_confidence', { defaultValue: 'Min Confidence:' })}
              </span>
              <Input
                value={minConfidence}
                onChange={(e) => setMinConfidence(e.target.value)}
                className="h-9 w-[80px]"
                inputMode="decimal"
              />
            </label>
            <Button
              size="sm"
              className="h-9 gap-1.5"
              onClick={handleRunMatching}
              disabled={
                running ||
                tagsLoading ||
                (!isDataCatalogMode &&
                  (!!tagsError || pidTags.length === 0 || tsTags.length === 0))
              }
            >
              {running ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Workflow className="size-4" />
              )}
              {running
                ? t('match_entity:running', { defaultValue: 'Running…' })
                : t('match_entity:run_matching', { defaultValue: 'Run Matching' })}
            </Button>
          </div>

          <MatchedFoundPanel
            matches={matches}
            selectedIds={selectedIds}
            decisions={decisions}
            onToggleSelect={handleToggleSelect}
            onToggleSelectAll={handleToggleSelectAll}
            onDecide={handleDecide}
          />
        </main>

        {/* Footer */}
        <footer className="flex h-[64px] shrink-0 items-center justify-between gap-3 border-t border-border bg-background px-6 dark:bg-dark-card">
          <Button variant="outline" size="sm" className="h-9 gap-1.5" disabled>
            <History className="size-4" />
            {t('match_entity:history', { defaultValue: 'History' })}
          </Button>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              className="h-9 gap-1.5"
              onClick={handleExportSelected}
              disabled={selectedIds.size === 0}
            >
              <Download className="size-4" />
              {t('match_entity:export_selected', {
                defaultValue: 'Export Selected ({{count}})',
                count: selectedIds.size,
              })}
            </Button>
            {isDataCatalogMode ? (
              <Button
                size="sm"
                className="h-9 gap-1.5"
                onClick={handleSubmitDecisions}
                disabled={unsavedDecisions.length === 0 || saving}
              >
                {saving ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
                {saving
                  ? t('match_entity:saving', { defaultValue: 'Saving…' })
                  : t('match_entity:save_decisions', {
                      defaultValue: 'Save Decisions ({{count}})',
                      count: unsavedDecisions.length,
                    })}
              </Button>
            ) : (
              <Button
                size="sm"
                className="h-9 gap-1.5"
                onClick={handleSaveSelected}
                disabled={
                  selectedIds.size === 0 ||
                  saving ||
                  !isWorkspaceSaveAllowed(isDataCatalogMode, workspaceId)
                }
              >
                {saving ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
                {saving
                  ? t('match_entity:saving', { defaultValue: 'Saving…' })
                  : t('match_entity:save_selected', {
                      defaultValue: 'Save Selected ({{count}})',
                      count: selectedIds.size,
                    })}
              </Button>
            )}
          </div>
        </footer>
      </div>
    </div>
  )
}

export default MatchEntityPage
